import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import React from "react";

import { AGENT_INFO } from "../../constants";

// Dave is the one executing a Best Deal, regardless of which agent (Iris,
// Regina, ...) sensed the issue that led to it. This block owns the deal
// facts and the Accept/Reject/Why CTAs so every agent's card presents the
// same action, under the same attribution, with the same copy — instead of
// each card re-implementing its own buttons under its own vocabulary.
const DaveDealAction = ({
  deal,
  currentPrice,
  acceptDisabled,
  rejectDisabled,
  confirmDisabled,
  rejectLoading,
  showDealConfirmation,
  onAcceptClick,
  onConfirmAccept,
  onCancelConfirmation,
  onReject,
  showRejectReasonInput,
  rejectReason,
  onRejectReasonChange,
  onConfirmReject,
  onCancelReject,
  onWhy,
  whyActive,
}) => {
  const dealLabel =
    deal.discount_percent != null
      ? `${deal.discount_percent}% Off Best Deal`
      : `${deal.deal_type || "Best Deal"} Available`;
  const startDate = deal.adjusted_start_date || deal.start_date;
  const endDate = deal.adjusted_end_date || deal.end_date;

  return (
    <>
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: "#e3f2fd",
          borderRadius: 2,
          border: "1px solid #90caf9",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.125,
            mb: 1,
          }}
        >
          <Box
            component="img"
            src={AGENT_INFO.dave.avatar}
            alt={AGENT_INFO.dave.name}
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: `2px solid ${AGENT_INFO.dave.color}`,
              backgroundColor: "#fff",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: AGENT_INFO.dave.color,
              fontWeight: 700,
              fontSize: "0.95rem",
            }}
          >
            {AGENT_INFO.dave.name}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "#1565c0", fontWeight: 600, mb: 1 }}
        >
          {dealLabel}
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
          Current Price: ${currentPrice}
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
          Deal Price:{" "}
          <strong style={{ color: "#2e7d32" }}>${deal.deal_price}</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: "#666" }}>
          {startDate} → {endDate}
        </Typography>
        {deal.dates_adjusted && (
          <Typography
            variant="caption"
            sx={{ color: "#ff9800", fontStyle: "italic", mt: 0.5 }}
          >
            * Dates adjusted to next available slot
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          size="small"
          disabled={acceptDisabled}
          sx={{
            backgroundColor: acceptDisabled ? "#e0e0e0" : "#4caf50",
            color: acceptDisabled ? "#9e9e9e" : "#fff",
            textTransform: "none",
            fontWeight: 500,
            boxShadow: "none",
            borderRadius: 3,
            "&:hover": !acceptDisabled
              ? { backgroundColor: "#45a049", boxShadow: "none" }
              : {},
          }}
          onClick={acceptDisabled ? undefined : onAcceptClick}
        >
          Accept
        </Button>

        <Button
          variant="outlined"
          size="small"
          disabled={rejectDisabled || showRejectReasonInput}
          sx={{
            color: rejectDisabled ? "#9e9e9e" : "#f44336",
            borderColor: rejectDisabled ? "#e0e0e0" : "#f44336",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 3,
            "&:hover": !rejectDisabled
              ? { borderColor: "#d32f2f", backgroundColor: "transparent" }
              : {},
          }}
          onClick={rejectDisabled ? undefined : onReject}
        >
          Reject
        </Button>

        {onWhy && (
          <Button
            variant={whyActive ? "contained" : "outlined"}
            size="small"
            onClick={onWhy}
            sx={{
              ...(whyActive
                ? {
                    backgroundColor: "#64b5f6",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#42a5f5" },
                  }
                : {
                    color: "#64b5f6",
                    borderColor: "#64b5f6",
                    "&:hover": {
                      borderColor: "#42a5f5",
                      backgroundColor: "transparent",
                    },
                  }),
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 20,
            }}
          >
            Why?
          </Button>
        )}
      </Box>

      {showDealConfirmation && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            Confirm scheduling this Best Deal?
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={confirmDisabled}
              sx={{
                backgroundColor: "#1976d2",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: "none",
                borderRadius: 3,
                "&:hover": { backgroundColor: "#1565c0", boxShadow: "none" },
              }}
              onClick={onConfirmAccept}
            >
              Yes, Schedule
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={confirmDisabled}
              sx={{
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 3,
              }}
              onClick={onCancelConfirmation}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}

      {showRejectReasonInput && (
        <>
          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={rejectReason}
            disabled={rejectLoading}
            onChange={(e) => onRejectReasonChange(e.target.value)}
            placeholder="Enter reject reason"
            variant="outlined"
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": { backgroundColor: "#ffffff" },
            }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={rejectLoading || !rejectReason}
              sx={{
                backgroundColor: "#f44336",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: "none",
                borderRadius: 3,
                "&:hover": { backgroundColor: "#d32f2f", boxShadow: "none" },
              }}
              onClick={onConfirmReject}
            >
              {rejectLoading ? "Processing..." : "Submit"}
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={rejectLoading}
              sx={{
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 3,
              }}
              onClick={onCancelReject}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default DaveDealAction;
